// Admin-only panel showing the Play-All / Hands-free funnel: how many
// users opt in, how many complete, where they pause / stop, and per-
// intervention breakdown. Data comes from /api/hands-free/stats which
// is auth-gated to admin users.

import { useEffect, useState } from "react";
import axios from "axios";
import { Speaker, Pause, RotateCw, Square, CheckCircle } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { format } from "date-fns";
import logger from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERVENTION_SHORT = {
  quiet_the_worry: "Worry",
  overcome_overwhelm: "Overwhelm",
  dial_down_tension: "Tension",
  healing_old_stories: "Old stories",
  beat_stage_fright: "Stage fright",
  fear_of_sleeplessness: "Sleeplessness",
  shake_off_sadness: "Sadness",
  boost_self_worth: "Self-worth",
  loosen_pride: "Pride",
  build_better_habits: "Habits",
  nightmare_reset: "Nightmares",
};

const formatLabel = (slug) => INTERVENTION_SHORT[slug] || slug;

const StatCard = ({ icon, label, value, color, bg }) => (
  <div
    className="bg-white border border-slate-100 rounded-xl p-4 flex items-center gap-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
    data-testid={`hf-stat-${label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
  >
    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: bg, color }}>
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-[#718096] truncate">{label}</p>
      <p className="text-xl font-outfit font-semibold text-[#1B2349]">{value}</p>
    </div>
  </div>
);

export const HandsFreeInsightsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/hands-free/stats`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        logger.warn("hands-free stats fetch failed", err?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center text-sm text-[#718096]" data-testid="hf-insights-loading">
        Loading hands-free analytics…
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center text-sm text-[#718096]" data-testid="hf-insights-error">
        Couldn't load hands-free analytics.
      </div>
    );
  }

  const completionPct = Math.round((data.completion_rate || 0) * 100);
  const chartData = (data.by_intervention || []).map((row) => ({
    name: formatLabel(row.intervention_type),
    opt_ins: row.opt_ins || 0,
    completes: row.completes || 0,
    paused: row.paused || 0,
    stopped: row.stopped || 0,
  }));

  return (
    <div className="space-y-6" data-testid="hf-insights-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Speaker className="w-5 h-5" />}
          label="Opt-ins"
          value={data.total_opt_ins}
          color="#3090A8"
          bg="#EAF0F9"
        />
        <StatCard
          icon={<CheckCircle className="w-5 h-5" />}
          label="Completes"
          value={`${data.total_completes} · ${completionPct}%`}
          color="#3F8A6B"
          bg="#E8F3EC"
        />
        <StatCard
          icon={<Pause className="w-5 h-5" />}
          label="Paused / Resumed"
          value={`${data.total_paused} / ${data.total_resumed}`}
          color="#C6884A"
          bg="#FBF1E1"
        />
        <StatCard
          icon={<Square className="w-5 h-5" />}
          label="Stopped early"
          value={data.total_stopped}
          color="#B65A5A"
          bg="#FBEAEA"
        />
      </div>

      {chartData.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
          <h3 className="font-outfit font-medium text-base text-[#1B2349] mb-4">
            By practice
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 12, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#EDF2F7" strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#718096" }} interval={0} angle={-15} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11, fill: "#718096" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12 }}
                cursor={{ fill: "rgba(48,144,168,0.06)" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="opt_ins" name="Opt-ins" fill="#3090A8" radius={[4, 4, 0, 0]} />
              <Bar dataKey="completes" name="Completes" fill="#3F8A6B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paused" name="Paused" fill="#C6884A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="stopped" name="Stopped" fill="#B65A5A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {(data.recent || []).length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
          <h3 className="font-outfit font-medium text-base text-[#1B2349] mb-4 flex items-center gap-2">
            <RotateCw className="w-4 h-4 text-[#3090A8]" />
            Most recent events
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="hf-recent-table">
              <thead className="text-left text-[#718096] text-xs uppercase tracking-wide">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Event</th>
                  <th className="py-2 pr-4">Practice</th>
                  <th className="py-2 pr-4">Task</th>
                </tr>
              </thead>
              <tbody className="text-[#1B2349]">
                {data.recent.slice(0, 25).map((ev) => (
                  <tr key={ev.event_id} className="border-t border-slate-100">
                    <td className="py-2 pr-4 text-[#718096]">
                      {ev.created_at ? format(new Date(ev.created_at), "MMM d · HH:mm") : "—"}
                    </td>
                    <td className="py-2 pr-4 font-medium">{ev.event_type}</td>
                    <td className="py-2 pr-4">{formatLabel(ev.intervention_type)}</td>
                    <td className="py-2 pr-4">{ev.task_index ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
